import { Component, OnInit } from "@angular/core";
import { IContentHeader } from "../../../layout/components/content-header/content-header.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { UtilsService } from "../../../auth/service/utils.service";
import { AuthenticationService } from "../../../auth/service";
import { Router } from "@angular/router";
import { ListService } from "../../../auth/service/list.service";
import { locale as italian } from "../../../i18n/it";

interface IPaymentMeasureUnit {
  name: string;
  id?: number;
}

@Component({
  selector: "app-payment-measure-unit",
  templateUrl: "./payment-measure-unit.component.html",
  styleUrls: ["./payment-measure-unit.component.scss"],
})
export class PaymentMeasureUnitComponent implements OnInit {
  public tableLimit: number = 10;
  public searchKeyword: string = "";
  public contentHeader: IContentHeader;
  public paymentMeasureUnit: IPaymentMeasureUnit = {
    name: null,
  };
  public paymentMeasureUnitList: IPaymentMeasureUnit[] = [];
  public filteredPaymentMeasureUnitList: IPaymentMeasureUnit[] = [];
  public showModalError: boolean = false;

  constructor(
    public _modalService: NgbModal,
    public _utilService: UtilsService,
    public _authenticationService: AuthenticationService,
    private router: Router,
    public _listService: ListService,
  ) {
    this.contentHeader = {
      headerTitle: italian.data.PAYMENTS_MEASURE_UNIT,
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: italian.data.MENU.MANAGE,
            isLink: true,
            link: "/dashboard/platform-management",
          },
          {
            name: italian.data.PAYMENTS_MEASURE_UNIT,
            isLink: false,
          },
        ],
      },
    };
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("show_menu_payment_measure_unit_list")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    await this.loadPaymentMeasureUnit();
  }

  async loadPaymentMeasureUnit() {
    this.paymentMeasureUnitList = await this._listService.getList("payment_measure_unit");
    this.paymentMeasureUnitFilter();
  }

  updateTableLimit(event: { target: { value: string } }) {
    this.tableLimit = Number(event.target.value);
  }

  paymentMeasureUnitFilter() {
    let items: IPaymentMeasureUnit[] = this.paymentMeasureUnitList;
    const keywords: string = this.searchKeyword.trim().toLowerCase();

    if (keywords.length > 0) {
      items = this.paymentMeasureUnitList.filter((item: IPaymentMeasureUnit) => item.name.toLowerCase().includes(this.searchKeyword.trim().toLowerCase()));
    }

    this.filteredPaymentMeasureUnitList = items;
  }

  public modalOpen(whichModal, paymentMeasureUnit: IPaymentMeasureUnit) {
    this.showModalError = false;
    this.paymentMeasureUnit = {
      name: null,
    };
    if (!!paymentMeasureUnit) {
      this.paymentMeasureUnit = {
        id: paymentMeasureUnit.id,
        name: paymentMeasureUnit.name,
      };
    }
    this._modalService.open(whichModal, {
      size: "sm",
      centered: true,
    });
  }

  isError(): boolean {
    if (!this.paymentMeasureUnit.name || this.paymentMeasureUnit.name.trim() === "" || this.paymentMeasureUnit.name.length < 1) {
      this.showModalError = true;
      return true;
    }
  }

  async deletePaymentMeasureUnit(currentPaymentMeasureUnit: { name: string; id?: number }) {
    if (!this._authenticationService.hasCapability("delete_payment_measure_unit")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += currentPaymentMeasureUnit.name;

    await this._utilService.showSWALDeleteConfirmation({ text: deleteText }).then(async (result) => {
      if (result.value) {
        await this._listService
          .delete(currentPaymentMeasureUnit.id, "payment_measure_unit")
          .then(async () => {
            await this._utilService.showPopupItemDeleted().then(async () => {
              await this.loadPaymentMeasureUnit();
            });
          })
          .catch(() => this._utilService.showSWALError({ text: "ERROR_PAYMENT_MEASURE_UNIT_REMOVE" }));
      } else if (result.isDismissed) {
        await this._utilService.showSWALError({
          title: "ABORTED_TITLE",
          text: "ABORTED_TEXT",
        });
      }
    });
  }

  async addPaymentMeasureUnit() {
    if (!this._authenticationService.hasCapability("create_payment_measure_unit")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    const data: IPaymentMeasureUnit = {
      name: this.paymentMeasureUnit.name.trim(),
    };

    this._utilService.showLoading();
    await this._listService
      .create(data, "payment_measure_unit")
      .then(() => {
        this.loadPaymentMeasureUnit();
        this._modalService.dismissAll();
        this._utilService.hideLoading();
        this.paymentMeasureUnitFilter();
        this._utilService.showSWALSuccess({ text: "SUCCESS_UPLOAD_PAYMENT_MEASURE_UNIT" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_PAYMENT_MEASURE_UNIT_INSERT" });
      });
  }

  async editPaymentMeasureUnit() {
    if (!this._authenticationService.hasCapability("edit_payment_measure_unit")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    const data: IPaymentMeasureUnit = {
      name: this.paymentMeasureUnit.name.trim(),
    };

    this._utilService.showLoading();
    await this._listService
      .update(this.paymentMeasureUnit.id, data, "payment_measure_unit")
      .then(() => {
        this._utilService.hideLoading();
        this.loadPaymentMeasureUnit();
        this.paymentMeasureUnitFilter();
        this._modalService.dismissAll();
        this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_PAYMENT_MEASURE_UNIT" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_PAYMENT_MEASURE_UNIT_EDIT" });
      });
  }
}
